/**
 * Helpers for deciding whether a time slot has already passed on the
 * currently-viewed calendar date. Used by the public booking pages to grey
 * out past slots so customers can't try to book a time that's already gone.
 *
 * "Slot has passed" rule:
 *   • If the booking_date is BEFORE today (Manila/local) → every slot is past.
 *   • If the booking_date is AFTER today → no slot is past.
 *   • If the booking_date is TODAY → a slot is past when the current local
 *     hour is greater than or equal to the slot's END hour. The slot
 *     containing the current moment STAYS clickable (during == OK).
 *
 * The check uses the browser's local clock. Almost all our customers are in
 * Asia/Manila and the venue itself is in Manila, so this matches reality.
 */

const _isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const _slotEndHour = (slotStr) => {
  // slotStr looks like "13:00 - 14:00". The end hour can be "00" which is
  // the midnight-rollover slot — treat that as 24 so it sorts correctly.
  const parts = String(slotStr || "").split("-").map((s) => s.trim());
  if (parts.length !== 2) return null;
  const endHour = parseInt(parts[1].split(":")[0], 10);
  if (Number.isNaN(endHour)) return null;
  return endHour === 0 ? 24 : endHour;
};

const _slotStartHour = (slotStr) => {
  const parts = String(slotStr || "").split("-").map((s) => s.trim());
  if (parts.length !== 2) return null;
  const startHour = parseInt(parts[0].split(":")[0], 10);
  return Number.isNaN(startHour) ? null : startHour;
};

/**
 * Returns true when `slot` on `dateStr` (YYYY-MM-DD) has already passed
 * relative to "now". Pass an explicit Date for tests; default = current.
 *
 * Note on the midnight-rollover slot: Assenda's operating window is
 * 7 AM – 1 AM (next day). The "00:00 - 01:00" slot is therefore stored
 * on the booking_date but actually represents the next morning. We treat
 * any slot whose start hour is BEFORE the operating window (< 7) as a
 * post-midnight rollover that is always in the future relative to a
 * same-day comparison.
 */
export const isSlotPast = (dateStr, slot, now = new Date()) => {
  if (!dateStr || !slot) return false;
  const today = _isoDate(now);
  if (dateStr < today) return true;
  if (dateStr > today) return false;
  // Same day. Post-midnight slot ("00:00 - 01:00") belongs to the next
  // morning — never past when viewed on the booking_date.
  const startHour = _slotStartHour(slot);
  if (startHour !== null && startHour < 7) return false;
  const endHour = _slotEndHour(slot);
  if (endHour === null) return false;
  return now.getHours() >= endHour;
};
